import React, { memo, useRef, useState, useCallback } from "react";
import { View, Pressable, Platform, ActivityIndicator } from "react-native";
import { Image } from "@/components/ui/Image";
import Svg, { Path } from "react-native-svg";
import { Feather } from "@expo/vector-icons";
import * as ImagePickerLib from "expo-image-picker";
import { AppText } from "@/components/ui/AppText";
import { Icon } from "@/components/ui/Icon";
import { Label } from "@/components/ui/Label";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ImageUploadState = "idle" | "dragover" | "uploading" | "success" | "error";

export interface ImageUploadProps {
  /** Field label rendered above the drop zone. */
  label?: TranslationKeys;
  /**
   * Called with an array of image URIs after the user selects images.
   * Trigger your upload logic here.
   */
  onImages: (uris: string[]) => void;
  /** External state (parent drives display). */
  uploadState?: ImageUploadState;
  /** 0–100 percentage shown during `uploading` state. */
  uploadProgress?: number;
  /**
   * Maximum number of images the user can select.
   * @default 16
   */
  maxImages?: number;
  /** Whether to allow selecting multiple images at once (native only). */
  allowMultiple?: boolean;
  /** Currently selected / uploaded image URIs used for a preview thumbnail strip. */
  currentUris?: string[];
  hint?: TranslationKeys;
  /** Validation / server error. */
  error?: TranslationKeys;
  disabled?: boolean;
  testID?: string;
  /**
   * Called when the user taps the trash icon on an image tile.
   * Use to remove that image from the selection.
   */
  onRemoveImage?: (uri: string, index: number) => void;
  /** When true, renders a red asterisk after the label. */
  required?: boolean;
  /**
   * When true, renders the image grid without upload controls, delete buttons,
   * or the add-more tile — suitable for review/read-only contexts.
   */
  readOnly?: boolean;
  containerClassName?: string;
}

// ─── Upload cloud SVG icon ────────────────────────────────────────────────────

const UploadCloudIcon = memo(function UploadCloudIcon() {
  return (
    <Svg width={20} height={20} viewBox="0 0 20 20" fill="none">
      <Path
        d="M13.333 13.333 10 10m0 0-3.333 3.333M10 10v7.5"
        stroke="#6C717A"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M16.975 15.308A4.375 4.375 0 0 0 15 6.875h-1.1A6.875 6.875 0 1 0 2.5 13.208"
        stroke="#6C717A"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
});

/**
 * Image grid matching Figma node 10007:271306 "Image / Filled".
 * Each existing image = full-size tile with dark overlay + centered white trash icon.
 * Final slot = dashed "add more" tile.
 */
const ImageGrid = memo(function ImageGrid({
  uris,
  onRemove,
  onAdd,
  disabled,
  readOnly = false,
  containerClassName,
}: {
  uris: string[];
  onRemove?: (uri: string, index: number) => void;
  onAdd?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  containerClassName?: string;
}) {
  const { t } = useI18n();
  return (
    <View className={cn("w-full flex-row flex-wrap gap-3", containerClassName)}>
      {uris.map((uri, i) => (
        <View
          key={`${i}-${uri}`}
          className="relative overflow-hidden rounded-lg"
          style={{ width: "48%", height: 170 }}
        >
          <Image
            source={{ uri }}
            style={{ width: "100%", height: "100%" }}
            contentFit="cover"
            accessibilityLabel={`Image ${i + 1}`}
          />
          {/* Dark overlay + trash icon — hidden in readOnly mode */}
          {!readOnly && (
            <View
              className="absolute inset-0 rounded-lg items-center justify-center"
              style={{ backgroundColor: "rgba(20,22,26,0.45)" }}
            >
              {!!onRemove && !disabled && (
                <Pressable
                  onPress={() => onRemove(uri, i)}
                  hitSlop={12}
                  accessibilityRole="button"
                  accessibilityLabel={t("imageUpload.removeImage", { index: i + 1 })}
                >
                  <Icon as={Feather} name="trash-2" size={32} className="text-white" />
                </Pressable>
              )}
            </View>
          )}
        </View>
      ))}

      {/* Add-more tile — dashed dark border with cloud icon */}
      {!!onAdd && !disabled && (
        <Pressable
          onPress={onAdd}
          accessibilityRole="button"
          accessibilityLabel={t("imageUpload.addMore")}
          className="rounded-lg items-center justify-center gap-3"
          style={{
            width: "48%",
            height: 170,
            borderWidth: 1,
            borderStyle: "dashed",
            borderColor: "#14161A",
            backgroundColor: "#FFFFFF",
          }}
        >
          <View className="size-11 rounded-full bg-stroke-50 items-center justify-center">
            <UploadCloudIcon />
          </View>
          <View className="items-center">
            <AppText weight="medium" className="text-base text-blue-50">
              {t("imageUpload.clickToUpload")}
            </AppText>
            {Platform.OS === "web" && (
              <AppText weight="medium" className="text-base text-dark-text-20">
                {t("imageUpload.orDragAndDrop")}
              </AppText>
            )}
          </View>
        </Pressable>
      )}
    </View>
  );
});

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * An image upload field with drag-and-drop on web and a system image picker on native.
 *
 * Mirrors the Figma "Input Field / Image" variant (node 10007:271269).
 *
 * - **Web:** dashed drop zone with native HTML drag events +
 *   `<input type="file" accept="image/*" multiple>` for click-to-upload.
 * - **Native:** opens `expo-image-picker` on press.
 *
 * `uploadState` and `uploadProgress` are controlled externally — call your
 * upload API inside `onImages` and then pass state/progress back down.
 *
 * @example
 * <ImageUpload
 *   label={t("vendor.form.productImages")}
 *   maxImages={16}
 *   allowMultiple
 *   onImages={handleImagesSelected}
 *   uploadState={state}
 *   currentUris={uploadedUris}
 *   error={fieldError}
 * />
 */
export const ImageUpload = memo(function ImageUpload({
  label,
  onImages,
  uploadState: externalState,
  uploadProgress: _uploadProgress = 0,
  maxImages = 16,
  allowMultiple = true,
  currentUris,
  hint,
  error,
  disabled = false,
  testID,
  onRemoveImage,
  required,
  readOnly = false,
  containerClassName,
}: ImageUploadProps) {
  const [internalDragOver, setInternalDragOver] = useState(false);
  const webInputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useI18n();

  const state: ImageUploadState = externalState ?? (internalDragOver ? "dragover" : "idle");

  // ── Native image picker ───────────────────────────────────────────────────

  const handleNativePick = useCallback(async () => {
    if (disabled) return;

    const { status } = await ImagePickerLib.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") return;

    const result = await ImagePickerLib.launchImageLibraryAsync({
      mediaTypes: ["images"],
      allowsMultipleSelection: allowMultiple,
      quality: 0.85,
      selectionLimit: maxImages,
    });

    if (result.canceled) return;
    onImages(result.assets.map((a) => a.uri));
  }, [disabled, allowMultiple, maxImages, onImages]);

  // ── Web file input ────────────────────────────────────────────────────────

  const handleWebChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (!files.length) return;
      const uris = files.map((f) => URL.createObjectURL(f));
      onImages(uris);
      e.target.value = "";
    },
    [onImages]
  );

  const triggerWebInput = useCallback(() => {
    if (!disabled) webInputRef.current?.click();
  }, [disabled]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setInternalDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setInternalDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setInternalDragOver(false);
      const files = Array.from(e.dataTransfer.files ?? []).filter((f) =>
        f.type.startsWith("image/")
      );
      if (!files.length) return;
      onImages(files.map((f) => URL.createObjectURL(f)));
    },
    [onImages]
  );

  // ── Styles ────────────────────────────────────────────────────────────────

  const hasImages = (currentUris?.length ?? 0) > 0;
  // Show the grid whenever images are present and not uploading
  const showGrid = (state === "success" || state === "idle") && hasImages;

  const borderClass = error
    ? "border-error"
    : state === "dragover"
      ? "border-blue-50"
      : "border-stroke-50";

  const bgClass = state === "dragover" ? "bg-blue-5" : showGrid ? "bg-white" : "bg-stroke-15";

  // Dashed border only for the idle/dragover upload target
  const dashedClass = !showGrid && state !== "uploading" ? "border-dashed" : "";

  const isInteractive = !disabled && state !== "uploading";

  const handleAddMore = Platform.OS === "web" ? triggerWebInput : handleNativePick;

  // ── Drop zone content ─────────────────────────────────────────────────────

  const dropZoneContent = showGrid ? (
    /*
     * Images selected — Figma node 10007:271306 "Image / Filled"
     * Full-size image tiles with dark overlay + trash, plus "add more" tile.
     */
    <ImageGrid
      uris={currentUris!}
      onRemove={!disabled ? onRemoveImage : undefined}
      onAdd={isInteractive ? handleAddMore : undefined}
      disabled={disabled}
    />
  ) : state === "uploading" ? (
    /* Uploading — centered spinner with percentage label */
    <View className="items-center justify-center gap-2">
      <View className="size-14 items-center justify-center">
        <ActivityIndicator size="large" color="#1F6CFF" />
        <View className="absolute inset-0 items-center justify-center pointer-events-none">
          <AppText weight="medium" className="text-xs text-dark-text-50">
            {_uploadProgress}%
          </AppText>
        </View>
      </View>
      <AppText weight="medium" className="text-sm text-supporting-text">
        Uploading images…
      </AppText>
    </View>
  ) : (
    /* Idle / drag-over */
    <>
      <View className="size-10 rounded-full bg-stroke-50 items-center justify-center mb-2">
        <UploadCloudIcon />
      </View>
      <View className="flex-row flex-wrap justify-center gap-1 mb-1">
        <AppText weight="medium" className="text-base text-blue-50">
          {t("imageUpload.clickToUpload")}
        </AppText>
        {Platform.OS === "web" && (
          <AppText weight="medium" className="text-base text-supporting-text">
            {t("imageUpload.orDragAndDrop")}
          </AppText>
        )}
      </View>
      <AppText className="text-sm text-dark-text-15 text-center tracking-[0.14px]">
        PNG or JPG (max. {maxImages} images)
      </AppText>
    </>
  );

  // ── readOnly: just the image grid, no upload affordance ───────────────────────

  if (readOnly) {
    return (
      <View className="w-full items-start" testID={testID}>
        {!!label && <Label label={label} required={required} />}
        {(currentUris?.length ?? 0) > 0 && (
          <ImageGrid uris={currentUris!} containerClassName={containerClassName} readOnly />
        )}
      </View>
    );
  }

  // ── Web ───────────────────────────────────────────────────────────────────

  if (Platform.OS === "web") {
    return (
      <View className="mb-4 w-full items-start" testID={testID}>
        {!!label && <Label label={label} required={required} />}

        {/* @ts-ignore — HTMLInputElement ref is web-only */}
        <input
          ref={webInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          multiple={allowMultiple}
          style={{ display: "none" }}
          onChange={handleWebChange as unknown as React.ChangeEventHandler<HTMLInputElement>}
        />

        {showGrid ? (
          /* Filled: plain container — tiles have their own styling */
          <div
            style={{ opacity: disabled ? 0.5 : 1 }}
            className={cn("w-full bg-white", containerClassName)}
          >
            {dropZoneContent}
          </div>
        ) : (
          /* Idle / uploading: styled drop zone */
          <div
            onClick={isInteractive ? triggerWebInput : undefined}
            onDragOver={
              isInteractive
                ? (handleDragOver as unknown as React.DragEventHandler<HTMLDivElement>)
                : undefined
            }
            onDragLeave={
              isInteractive
                ? (handleDragLeave as unknown as React.DragEventHandler<HTMLDivElement>)
                : undefined
            }
            onDrop={
              isInteractive
                ? (handleDrop as unknown as React.DragEventHandler<HTMLDivElement>)
                : undefined
            }
            style={{ cursor: isInteractive ? "pointer" : "default", opacity: disabled ? 0.5 : 1 }}
            className={cn(
              "flex flex-col items-center justify-center h-[140px] w-full border rounded-md px-4 py-2 ",
              dashedClass,
              bgClass,
              borderClass,
              containerClassName
            )}
          >
            {dropZoneContent}
          </div>
        )}

        {!!hint && !error && (
          <AppText className="text-xs text-dark-text-10 mt-1 text-start">{hint}</AppText>
        )}
        {!!error && (
          <AppText role="alert" className="text-xs text-error mt-1 text-start">
            {error}
          </AppText>
        )}
      </View>
    );
  }

  // ── Native ────────────────────────────────────────────────────────────────

  return (
    <View className="mb-4 w-full items-start" testID={testID}>
      {!!label && <Label label={label} required={required} />}

      {showGrid ? (
        /* Filled: plain container — ImageGrid tiles are the interactive elements */
        <View className={cn("w-full bg-white", disabled ? "opacity-50" : "", containerClassName)}>
          {dropZoneContent}
        </View>
      ) : (
        /* Idle / uploading: full-width tappable drop zone */
        <Pressable
          onPress={isInteractive ? handleNativePick : undefined}
          disabled={!isInteractive}
          accessibilityRole="button"
          accessibilityLabel={label ? t(label) : t("imageUpload.a11y.uploadImages")}
          className={cn(
            "w-full flex-col items-center justify-center h-[140px] border rounded-md px-4 py-2",
            dashedClass,
            bgClass,
            borderClass,
            disabled ? "opacity-50" : "",
            containerClassName
          )}
          style={({ pressed }) => [{ opacity: pressed && isInteractive ? 0.7 : 1 }]}
        >
          {dropZoneContent}
        </Pressable>
      )}

      {!!hint && !error && (
        <AppText className="text-xs text-dark-text-10 mt-1 text-start">{t(hint)}</AppText>
      )}
      {!!error && (
        <AppText role="alert" className="text-xs text-error mt-1 text-start">
          {t(error)}
        </AppText>
      )}
    </View>
  );
});
